import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import publicApiClient from '@services/api/publicClient'
import { conversationService } from '@services/api/messages'
import { storageURL, mediaUrl } from '@config'
import headerImage from '@assets/images/header_accueil.png'
import avatarPlaceholder from '@assets/images/image_01.png'
import { CATEGORIES } from '@constants/categories'
import { useAuthStore } from '@store/authStore'
import { encodeId } from '@utils/idCipher'

function DashboardPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()

  useEffect(() => {
    document.title = 'KUDHI Market'
    return () => { document.title = 'KUDHI' }
  }, [])
  const [activeCategory, setActiveCategory] = useState(null)
  const [activeTab, setActiveTab] = useState('articles') // Nouvel état pour les onglets

  // Fonction pour formater les prix en FCFA
  const formatPrice = (price) => {
    const priceNum = typeof price === 'string' ? parseFloat(price.replace(/[^0-9]/g, '')) : price
    if (!priceNum || priceNum === 0) return ''
    return new Intl.NumberFormat('fr-FR').format(priceNum) + ' FCFA'
  }
  const [videoCarouselIndex, setVideoCarouselIndex] = useState(0)
  const [bannerCarouselIndex, setBannerCarouselIndex] = useState(0)
  const [discountCarouselIndex, setDiscountCarouselIndex] = useState(0)
  const [isFlashResetting, setIsFlashResetting] = useState(false)
  const [showNewPostModal, setShowNewPostModal] = useState(false)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [showCategories, setShowCategories] = useState(false)
  const [currentPage, setCurrentPage] = useState(3)
  const [videos, setVideos] = useState([])
  const [loadingVideos, setLoadingVideos] = useState(true)
  const [banners, setBanners] = useState([])
  const [loadingBanners, setLoadingBanners] = useState(true)
  const [adBanners, setAdBanners] = useState([]) // Bannières publicitaires
  const [loadingAdBanners, setLoadingAdBanners] = useState(true)
  const [adVideos, setAdVideos] = useState([]) // Vidéos publicitaires
  const [loadingAdVideos, setLoadingAdVideos] = useState(true)
  const [adPosts, setAdPosts] = useState([]) // Publications publicitaires
  const [loadingAdPosts, setLoadingAdPosts] = useState(true)
  const [adBannerIndex, setAdBannerIndex] = useState(0)
  const [adVideoIndex, setAdVideoIndex] = useState(0)
  const [adPostIndex, setAdPostIndex] = useState(0)
  const [isAdPostHovered, setIsAdPostHovered] = useState(false) // Pour arrêter le carrousel au hover
  const [recentContacts, setRecentContacts] = useState([])
  const [loadingContacts, setLoadingContacts] = useState(true)

  // Charger les bannières depuis l'API
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setLoadingBanners(true)
        const response = await publicApiClient.get('/posts/type', { params: { type: 3, exclude_staff: 1 } })
        const data = response.data || []
        setBanners(data.map(b => ({
          banniere_id: b.post_id,
          titre: b.libele,
          lien_url: b.siteWeb,
          image_url: b.fichiers?.[0]?.chemin ? mediaUrl(b.fichiers[0].chemin) : null,
        })))
      } catch {
        setBanners([])
      } finally {
        setLoadingBanners(false)
      }
    }
    fetchBanners()
  }, [])

  // Charger les vidéos depuis l'API
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoadingVideos(true)
        const isVideoFile = (path) => /\.(mp4|mov|avi|webm|mkv)$/i.test(path)
        const response = await publicApiClient.get('/posts/type', { params: { type: 1, exclude_staff: 1 } })
        const data = response.data || []
        setVideos(data.map(v => ({
          id: v.post_id,
          titre: v.libele,
          description: v.description,
          views: v.vues || 0,
          likes: v.post_likes_count ?? v.likes ?? 0,
          comments: v.post_comments_count ?? v.comments ?? 0,
          video_url: (() => {
            const f = v.fichiers?.find(f => isVideoFile(f.chemin))
            return f ? mediaUrl(f.chemin) : null
          })(),
          thumbnail_url: (() => {
            const f = v.fichiers?.find(f => !isVideoFile(f.chemin))
            return f ? mediaUrl(f.chemin) : null
          })(),
        })))
      } catch {
        setVideos([])
      } finally {
        setLoadingVideos(false)
      }
    }
    fetchVideos()
  }, [])

  // Charger les bannières publicitaires (publiées par Admin/Super Admin/Staff)
  useEffect(() => {
    const fetchAdBanners = async () => {
      try {
        setLoadingAdBanners(true)
        const response = await publicApiClient.get('/posts/type', { params: { type: 3, staff_only: 1 } })
        const data = response.data || []
        setAdBanners(data.map(b => ({
          banniere_id: b.post_id,
          id: b.post_id,
          titre: b.libele,
          lien_url: b.siteWeb,
          image_url: b.fichiers?.[0]?.chemin ? mediaUrl(b.fichiers[0].chemin) : null,
          views: b.vues || 0,
          likes: b.post_likes_count ?? b.likes ?? 0,
          comments: b.post_comments_count ?? 0,
        })))
      } catch {
        setAdBanners([])
      } finally {
        setLoadingAdBanners(false)
      }
    }
    fetchAdBanners()
  }, [])

  // Charger les vidéos publicitaires (publiées par Admin/Super Admin/Staff)
  useEffect(() => {
    const fetchAdVideos = async () => {
      try {
        setLoadingAdVideos(true)
        const isVideoFile = (path) => /\.(mp4|mov|avi|webm|mkv)$/i.test(path)
        const response = await publicApiClient.get('/posts/type', { params: { type: 1, staff_only: 1 } })
        const data = response.data || []
        setAdVideos(data.map(v => ({
          id: v.post_id,
          titre: v.libele,
          description: v.description,
          views: v.vues || 0,
          likes: v.post_likes_count ?? v.likes ?? 0,
          comments: v.post_comments_count ?? 0,
          video_url: (() => {
            const f = v.fichiers?.find(f => isVideoFile(f.chemin))
            return f ? mediaUrl(f.chemin) : null
          })(),
          thumbnail_url: (() => {
            const f = v.fichiers?.find(f => !isVideoFile(f.chemin))
            return f ? mediaUrl(f.chemin) : null
          })(),
        })))
      } catch {
        setAdVideos([])
      } finally {
        setLoadingAdVideos(false)
      }
    }
    fetchAdVideos()
  }, [])

  // Charger les publications publicitaires (publiées par Admin/Super Admin/Staff)
  useEffect(() => {
    const fetchAdPosts = async () => {
      try {
        setLoadingAdPosts(true)
        const response = await publicApiClient.get('/posts/type', { params: { type: 2, staff_only: 1, limit: 100 } })
        const data = response.data || []
        setAdPosts(data.map(p => {
          const prixNum = parseFloat(String(p.prix || 0).replace(/[^0-9.]/g, '')) || 0
          const newPriceNum = parseFloat(String(p.new_price || 0).replace(/[^0-9.]/g, '')) || 0
          const marge = parseInt(String(p.marge_reduite ?? '').replace(/[^0-9]/g, '')) || 0
          const { dealPrice, oldPrice, reductionPct } = resolvePricing(prixNum, newPriceNum, marge, p)
          const oldPriceNum = oldPrice || 0
          const conditionRaw = p.etat_appareil || p.condition || ''
          const conditionLabels = {
            // valeurs lowercase (articles standards)
            neuf: 'Neuf', scelle: 'Scellé', reconditionne: 'Reconditionné',
            quasi_neuf: 'Quasi neuf', excellent: 'Excellent',
            bon: 'Bon état', moyen: 'État moyen', usage: 'Usagé',
            // valeurs supplémentaires (admin)
            occasion: 'Occasion', 'reconditionné': 'Reconditionné',
          }
          const conditionKey = conditionRaw.toLowerCase().replace(/é/g, 'e').replace(/\s/g, '_')
          const etat_label = conditionLabels[conditionRaw.toLowerCase()] || conditionLabels[conditionKey] || (conditionRaw || '')
          return {
            id: p.post_id,
            libele: p.libele,
            price: dealPrice,
            oldPrice: oldPriceNum > 0 ? oldPriceNum : null,
            discount: reductionPct,
            etat_appareil: conditionRaw.toLowerCase(),
            etat_label: etat_label,
            vues: p.vues || 0,
            likes_count: p.post_likes_count ?? p.likes ?? 0,
            comments_count: p.post_comments_count ?? 0,
            images: p.fichiers?.map(f => mediaUrl(f.chemin)) || [],
          }
        }))
      } catch {
        setAdPosts([])
      } finally {
        setLoadingAdPosts(false)
      }
    }
    fetchAdPosts()
  }, [])

  // Charger les contacts récents (issus des conversations de l'utilisateur)
  useEffect(() => {
    if (!user?.client_id) {
      setRecentContacts([])
      setLoadingContacts(false)
      return
    }

    const fetchContacts = async () => {
      try {
        setLoadingContacts(true)
        const res = await conversationService.getConversations()
        if (res.success) {
          const contacts = (res.data || [])
            .filter(c => c.other_client)
            .slice(0, 8)
            .map(c => ({
              id:         c.other_client.client_id,
              first_name: c.other_client.prenom || '',
              name:       c.other_client.nom || '',
              full_name:  [c.other_client.prenom, c.other_client.nom].filter(Boolean).join(' ') || 'Utilisateur',
              phone:      c.other_client.numero || '',
              avatar:     c.other_client.photo_profil || null,
            }))
          setRecentContacts(contacts)
        }
      } catch {
        setRecentContacts([])
      } finally {
        setLoadingContacts(false)
      }
    }

    fetchContacts()
  }, [user])

  // Carrousel automatique pour la bannière
  useEffect(() => {
    if (banners.length > 0) {
      const interval = setInterval(() => {
        setBannerCarouselIndex((prevIndex) => (prevIndex + 1) % banners.length)
      }, 5000) // Change toutes les 5 secondes
      return () => clearInterval(interval)
    }
  }, [banners])

  // Carrousel automatique pour les vidéos
  useEffect(() => {
    if (videos.length > 0) {
      const interval = setInterval(() => {
        setVideoCarouselIndex((prevIndex) => (prevIndex + 1) % videos.length)
      }, 8000) // Change toutes les 8 secondes
      return () => clearInterval(interval)
    }
  }, [videos])

  // Carrousel automatique pour les bannières publicitaires
  useEffect(() => {
    if (adBanners.length > 0) {
      const interval = setInterval(() => {
        setAdBannerIndex((prevIndex) => (prevIndex + 1) % adBanners.length)
      }, 15000) // Change toutes les 15 secondes
      return () => clearInterval(interval)
    }
  }, [adBanners])

  // Carrousel automatique pour les vidéos publicitaires
  useEffect(() => {
    if (adVideos.length > 0) {
      const interval = setInterval(() => {
        setAdVideoIndex((prevIndex) => (prevIndex + 1) % adVideos.length)
      }, 15000) // Change toutes les 15 secondes
      return () => clearInterval(interval)
    }
  }, [adVideos])

  // Carrousel automatique pour les publications publicitaires (s'arrête au hover)
  useEffect(() => {
    if (adPosts.length > 0 && !isAdPostHovered) {
      const interval = setInterval(() => {
        setAdPostIndex((prevIndex) => (prevIndex + 1) % adPosts.length)
      }, 6000) // Change toutes les 6 secondes
      return () => clearInterval(interval)
    }
  }, [adPosts, isAdPostHovered])

  // Images bannières par défaut si aucune bannière n'est chargée
  const defaultBannerImage = headerImage

  // Composants d'icônes SVG
  const CategoryIcon = ({ id }) => {
    const icons = {
      1: ( // Toutes les catégories
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z"/>
        </svg>
      ),
      2: ( // Auto - Moto
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
        </svg>
      ),
      3: ( // Immobilier
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
        </svg>
      ),
      4: ( // Informatique
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z"/>
        </svg>
      ),
      5: ( // Vêtements
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 4l1.29 2.57L20 7.86 16.29 10l-.86 2.86L12 11 8.57 12.86 7.71 10 4 7.86l2.71-1.29L8 4h8m0-2H8L6 5 2 7l5 3-1 4 6-2 6 2-1-4 5-3-4-2-2-3z"/>
        </svg>
      ),
      6: ( // Loisirs
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
      ),
      7: ( // Services
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
        </svg>
      ),
      8: ( // Décorations
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 22C6.49 22 2 17.51 2 12S6.49 2 12 2s10 4.04 10 9c0 3.31-2.69 6-6 6h-1.77c-.28 0-.5.22-.5.5 0 .12.05.23.13.33.41.47.64 1.06.64 1.67 0 1.38-1.12 2.5-2.5 2.5zm0-18c-4.41 0-8 3.59-8 8s3.59 8 8 8c.28 0 .5-.22.5-.5 0-.16-.08-.28-.14-.35-.41-.46-.63-1.05-.63-1.65 0-1.38 1.12-2.5 2.5-2.5H16c2.21 0 4-1.79 4-4 0-3.86-3.59-7-8-7z"/><circle cx="6.5" cy="11.5" r="1.5"/><circle cx="9.5" cy="7.5" r="1.5"/><circle cx="14.5" cy="7.5" r="1.5"/><circle cx="17.5" cy="11.5" r="1.5"/>
        </svg>
      ),
      9: ( // Modes
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm9 7h-6v13h-2v-6h-2v6H9V9H3V7h18v2z"/>
        </svg>
      ),
      10: ( // Bijoux
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5L2 9l10 12L22 9l-3-6zM9.62 8l1.5-3h1.76l1.5 3H9.62zM11 10v6.68L5.44 10H11zm2 0h5.56L13 16.68V10z"/>
        </svg>
      ),
      11: ( // Sacs
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18 6h-2c0-2.21-1.79-4-4-4S8 3.79 8 6H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-8 4c0 .55-.45 1-1 1s-1-.45-1-1V8h2v2zm2-6c1.1 0 2 .9 2 2h-4c0-1.1.9-2 2-2zm4 6c0 .55-.45 1-1 1s-1-.45-1-1V8h2v2z"/>
        </svg>
      ),
      12: ( // Chaussures
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12.65 3.5C10.33 2.91 7 3.5 7 3.5V4c0 1.66-1.34 3-3 3v1c0 2.76 2.24 5 5 5 1.38 0 2.63-.56 3.54-1.46L21 20v-2l-5-5V7.5c0-2.21-1.79-4-4-4l-.35.05z"/>
        </svg>
      ),
      13: ( // Chemises (ajout comme alternative)
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 4l1.29 2.57L20 7.86 16.29 10l-.86 2.86L12 11 8.57 12.86 7.71 10 4 7.86l2.71-1.29L8 4h8z"/>
        </svg>
      ),
      14: ( // Autres
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z"/>
        </svg>
      ),
    }
    return icons[id] || icons[14]
  }

  // Catégories complètes pour LIGDI

  // Catégories dynamiques depuis l'API
  const [categoriesFromDB, setCategoriesFromDB] = useState([])
  const [loadingCategories, setLoadingCategories] = useState(true)

  // Charger les catégories depuis l'API (filtrées selon CATEGORIES)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true)
        const response = await publicApiClient.get('/categories')
        const data = response.data || []
        const filtered = data.filter(c =>
          c.libele_categorie !== 'Toutes les catégories'
        )
        setCategoriesFromDB([
          { categorie_id: null, libele_categorie: 'Toutes les catégories', icon: 'apps' },
          ...filtered,
        ])
      } catch {
        setCategoriesFromDB([])
      } finally {
        setLoadingCategories(false)
      }
    }
    fetchCategories()
  }, [])

  // Produits dynamiques depuis l'API

  const [products, setProducts] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const pageSize = 12
  const prevCategoryRef = useRef(activeCategory)

  const CONDITION_LABELS = {
    neuf:          'Neuf',
    scelle:        'Scellé',
    reconditionne: 'Reconditionné',
    quasi_neuf:    'Quasi neuf',
    excellent:     'Excellent',
    bon:           'Bon état',
    moyen:         'État moyen',
    usage:         'Usagé',
  }

  // Autoplay carrousel Ventes Flash — boucle infinie via clones
  useEffect(() => {
    const discountedProducts = products.filter(p => p.oldPrice)
    const N = discountedProducts.length
    if (N < 4) return
    const timer = setInterval(() => {
      setDiscountCarouselIndex(i => {
        const next = i + 1
        if (next >= N) {
          // On entre dans les clones : reset silencieux après la transition
          setTimeout(() => {
            setIsFlashResetting(true)
            setDiscountCarouselIndex(0)
            setTimeout(() => setIsFlashResetting(false), 50)
          }, 520)
        }
        return next
      })
    }, 3500)
    return () => clearInterval(timer)
  }, [products])

  const parseReduction = (r) => {
    if (r === null || r === undefined || r === '' || r === 'Choisissez un champ') return null
    const num = parseInt(String(r).replace(/[^0-9]/g, ''))
    return isNaN(num) || num <= 0 || num >= 100 ? null : num
  }

  // Détermine le prix affiché (deal), l'ancien prix (barré) et le % de réduction.
  // Gère la convention actuelle (prix=original, new_price=prix réduit, marge_reduite=%)
  // ET l'ancienne (new_price = ancien prix) pour les données déjà en base.
  const resolvePricing = (prixNum, newPriceNum, marge, p) => {
    if (marge > 0 && newPriceNum > 0 && newPriceNum < prixNum) {
      return { dealPrice: newPriceNum, oldPrice: prixNum, reductionPct: marge }
    }
    if (newPriceNum > prixNum && newPriceNum > 0) {
      return { dealPrice: prixNum, oldPrice: newPriceNum, reductionPct: Math.round((1 - prixNum / newPriceNum) * 100) }
    }
    const r = parseReduction(p?.reduction ?? p?.discount)
    if (r > 0 && prixNum > 0) {
      return { dealPrice: prixNum, oldPrice: Math.round(prixNum / (1 - r / 100)), reductionPct: r }
    }
    return { dealPrice: prixNum, oldPrice: null, reductionPct: 0 }
  }

  const mapProduct = (p) => {
    const prixNum     = parseFloat(String(p.prix).replace(/[^0-9.]/g, '')) || 0
    const newPriceNum = p.new_price ? parseFloat(String(p.new_price).replace(/[^0-9.]/g, '')) : 0
    const marge       = parseInt(String(p.marge_reduite ?? '').replace(/[^0-9]/g, '')) || 0
    const conditionKey = p.etat_appareil || ''
    const { dealPrice, oldPrice, reductionPct } = resolvePricing(prixNum, newPriceNum, marge, p)
    return {
      id:                   p.post_id,
      name:                 p.libele,
      price:                dealPrice,
      oldPrice:             oldPrice,
      reduction:            reductionPct,
      etat_appareil:        conditionKey,
      etat_appareil_label:  CONDITION_LABELS[conditionKey] || '',
      image:                (() => { const f = p.fichiers?.find(f => f.is_main) ?? p.fichiers?.[0]; return f?.chemin ? mediaUrl(f.chemin) : null })(),
      views:                p.vues || 0,
      likes:                p.post_likes_count ?? p.likes ?? 0,
      comments:             p.post_comments_count ?? p.comments ?? 0,
      client_id:            p.client?.client_id || p.post_client_id || null,
      client_name:          p.client ? [p.client.nom, p.client.prenom].filter(Boolean).join(' ') : '',
      client_phone:         p.client?.numero || '',
      client_photo:         p.client?.photo_profil ? mediaUrl(p.client.photo_profil) : null,
      category_id:          p.post_categorie_id,
      date:                 p.created_at
                              ? new Date(p.created_at).toLocaleDateString('fr-FR', { day:'2-digit', month:'short', year:'numeric' })
                              : '',
    }
  }

  // Un seul effet — reset page si catégorie change, annule les requêtes obsolètes
  useEffect(() => {
    // Si la catégorie vient de changer et qu'on n'est pas à la page 1 :
    // on reset la page → le changement de `page` va re-déclencher cet effet
    if (prevCategoryRef.current !== activeCategory) {
      prevCategoryRef.current = activeCategory
      if (page !== 1) {
        setPage(1)
        return
      }
    }

    const controller = new AbortController()

    const load = async () => {
      try {
        setLoadingProducts(true)
        const params = { page, limit: pageSize, type: 2, status: 2, exclude_staff: 1 }
        if (activeCategory !== null) params.post_categorie_id = activeCategory
        const response = await publicApiClient.get('/posts', { params, signal: controller.signal })
        const posts = response.data || []
        setProducts(Array.isArray(posts) ? posts.map(mapProduct) : [])
        setTotalPages(Math.ceil((response.total || posts.length || 0) / pageSize))
      } catch (err) {
        // Ignorer les erreurs d'annulation (requête obsolète)
        if (err?.code !== 'ERR_CANCELED' && err?.name !== 'CanceledError') {
          setProducts([])
          setTotalPages(1)
        }
      } finally {
        setLoadingProducts(false)
      }
    }

    load()
    return () => controller.abort()
  }, [page, activeCategory])

  // Fonction pour changer de page avec AJAX
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
      setPage(newPage)
      // Scroll vers le haut de la liste des produits
      window.scrollTo({ top: 400, behavior: 'smooth' })
    }
  }

  // Contacts

  const contacts = [
    { name: 'Alain Yahaut', avatar: avatarPlaceholder },
    { name: 'Romaric Konan', avatar: avatarPlaceholder },
    { name: 'Paul Kouamé', avatar: avatarPlaceholder },
    { name: 'Alain Koffi', avatar: avatarPlaceholder },
  ]

  // Vérifier si l'utilisateur est admin, super admin ou staff
  const isAdminOrStaff = user && ['Admin', 'Super Admin', 'Staff'].includes(user.role)

  return (
    <div className="bg-gray-100 min-h-screen">

      {/* Bannière vidéo des annonces publiées */}

      <header className="relative w-full h-64 sm:h-80 md:h-96 lg:h-[600px] xl:h-[650px] bg-black">
        {loadingVideos ? (
          <div className="relative w-full h-full flex items-center justify-center">
            <div className="text-center text-gray-400">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto mb-4"></div>
              <p className="text-sm md:text-base">Chargement des vidéos...</p>
            </div>
          </div>
        ) : videos.length > 0 ? (
          <div className="relative w-full h-full">
            {videos[videoCarouselIndex]?.video_url ? (
              <video
                key={videos[videoCarouselIndex].id}
                className="w-full h-full object-cover cursor-pointer"
                autoPlay
                muted
                loop
                playsInline
                onClick={() => navigate(`/video/${encodeId(videos[videoCarouselIndex]?.id)}`)}
              >
                <source src={videos[videoCarouselIndex].video_url} type="video/mp4" />
                Votre navigateur ne supporte pas la lecture de vidéos.
              </video>
            ) : (
              <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                <p className="text-gray-400">Vidéo non disponible</p>
              </div>
            )}
            
            {/* Bouton - en haut à droite */}

            <div className="absolute top-6 right-6">
              <button
                onClick={() => navigate(`/video/${encodeId(videos[videoCarouselIndex]?.id)}`)}
                className="px-6 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-colors font-medium shadow-lg"
              >
                Voir la vidéo
              </button>
            </div>

            {/* Stats vues / likes / commentaires */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent pb-4 pt-12 px-4">
              <div className="flex items-center gap-4 text-white text-sm">
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span className="font-semibold">{videos[videoCarouselIndex]?.views ?? 0}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  <span className="font-semibold">{videos[videoCarouselIndex]?.likes ?? 0}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span className="font-semibold">{videos[videoCarouselIndex]?.comments ?? 0}</span>
                </div>
              </div>
            </div>

            {/* Indicateurs de pagination */}

            {videos.length > 1 && (
              <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2">
                {videos.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setVideoCarouselIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === videoCarouselIndex
                        ? 'bg-[#D4AF37] w-6'
                        : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="relative w-full h-full flex items-center justify-center">
            <div className="text-center text-gray-400">
              <svg className="w-20 h-20 mx-auto mb-4 opacity-30" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
              <p className="text-sm md:text-base">Aucune vidéo disponible</p>
            </div>
          </div>
        )}
      </header>


      {/* Bouton création de poste */}
      <button
        onClick={() => setShowNewPostModal(true)}
        className="group fixed bottom-4 right-4 sm:bottom-6 sm:right-6 md:bottom-8 md:right-8 w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] text-white rounded-full shadow-xl hover:shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 z-50 animate-pulse hover:animate-none"
      >
        <svg className="w-6 h-6 sm:w-7 sm:h-7 group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        <span className="absolute inset-0 rounded-full border-2 border-[#D4AF37] animate-ping opacity-75"></span>
        <span className="absolute inset-0 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-[#c09d2a]/20 group-hover:scale-150 transition-transform duration-500"></span>
      </button>

      {/* Main Content */}

      <div className="bg-gray-100">
        <div className="w-full md:w-[80%] mx-auto px-2 sm:px-4 py-4 mt-5">
          {/* Bannière publicitaire carrousel */}
          <div className="relative bg-white rounded-xl overflow-hidden shadow-sm mb-8">
            {loadingBanners ? (
              <div className="h-[180px] sm:h-[240px] md:h-[320px] lg:h-[380px] flex items-center justify-center bg-gray-200">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
              </div>
            ) : banners.length > 0 ? (
              <div
                onClick={() => {
                  const b = banners[bannerCarouselIndex]
                  if (b?.banniere_id) navigate(`/banner/${encodeId(b.banniere_id)}`)
                }}
                className="block cursor-pointer"
              >
                <div className="relative bg-gray-100 h-[180px] sm:h-[240px] md:h-[320px] lg:h-[380px] flex items-center justify-center">
                  <img
                    key={bannerCarouselIndex}
                    src={banners[bannerCarouselIndex]?.image_url || defaultBannerImage}
                    alt={banners[bannerCarouselIndex]?.titre || 'Banner'}
                    className="w-full h-full object-cover transition-all duration-700 ease-in-out animate-fadeIn"
                    onError={(e) => {
                      e.target.src = defaultBannerImage
                    }}
                  />
                  {/* Indicateurs de pagination */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {banners.map((_, index) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setBannerCarouselIndex(index)
                        }}
                        className={`transition-all duration-300 rounded-full ${
                          index === bannerCarouselIndex
                            ? 'bg-[#D4AF37] w-6 h-2'
                            : 'bg-gray-400 w-2 h-2 hover:bg-gray-500'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative h-[180px] sm:h-[240px] md:h-[320px] lg:h-[380px]">
                <img
                  src={defaultBannerImage}
                  alt="Default Banner"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>

          {/* Bloc catégorie + recherche - visible sur mobile aussi */}
          {/* Boutons Catégorie et Recherche - Mobile */}

          <div className="lg:hidden mb-4 flex items-center gap-3">
            <button
              onClick={() => setShowCategories(!showCategories)}
              className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center flex-shrink-0"
            >
              <svg width="24" height="24" viewBox="0 0 32 32" fill="white">
                <path d="M0,0V14.61H14.61V0ZM17.266,0V14.61h14.61V0Zm2.656,2.656h9.3v9.3h-9.3ZM0,17.266v14.61H14.61V17.266Zm17.266,0v14.61h14.61V17.266Z"/>
              </svg>
            </button>
            
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Rechercher ici..."
                className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
              />
              <button className="absolute right-3 top-1/2 -translate-y-1/2">
                <svg width="20" height="20" fill="#D4AF37" viewBox="0 0 24 24">
                  <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Menu déroulant des catégories - Mobile (position relative au bouton) */}

          {showCategories && (
            <>
              <div 
                className="fixed inset-0 bg-black/20 z-30 lg:hidden"
                onClick={() => setShowCategories(false)}
              />
              <div className="fixed top-32 left-4 w-64 bg-white rounded-xl shadow-2xl z-40 max-h-[60vh] overflow-y-auto lg:hidden">
                <div className="p-4 space-y-1">
                  {loadingCategories ? (
                    <div className="text-center py-4 text-gray-500">Chargement...</div>
                  ) : categoriesFromDB.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">Aucune catégorie</div>
                  ) : (
                    categoriesFromDB.map((cat) => (
                      <button
                        key={cat.categorie_id || 'all'}
                        onClick={() => {
                          setActiveCategory(cat.categorie_id)
                          setShowCategories(false)
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm rounded-lg transition-colors ${
                          activeCategory === cat.categorie_id
                            ? 'bg-[#D4AF37] text-white'
                            : 'text-[#001F3F] hover:bg-gray-100'
                        }`}
                      >
                        {cat.icon ? (
                          <span className="material-icons text-xl flex-shrink-0">{cat.icon}</span>
                        ) : (
                          <span className="text-xl flex-shrink-0">📋</span>
                        )}
                        <span className="font-medium text-left flex-1">{cat.libele_categorie}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </>
          )}


          <div className="flex flex-col lg:flex-row gap-4">

            {/* Colonne Catégories - Desktop uniquement */}

            <div className="hidden lg:block w-full lg:w-72 flex-shrink-0 sticky top-24 self-start">
              <div className="bg-white rounded-xl shadow-sm p-4 space-y-2 max-h-[calc(100vh-7rem)] overflow-y-auto scrollbar-hide">
                {loadingCategories ? (
                  <div className="text-center py-4 text-gray-500">Chargement...</div>
                ) : categoriesFromDB.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">Aucune catégorie</div>
                ) : (
                  categoriesFromDB.map((cat) => (
                    <button
                      key={cat.categorie_id || 'all'}
                      onClick={() => setActiveCategory(cat.categorie_id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm rounded-lg transition-colors ${
                        activeCategory === cat.categorie_id
                          ? 'bg-[#D4AF37] text-white'
                          : 'text-[#001F3F] hover:bg-gray-50'
                      }`}
                    >
                      {cat.icon ? (
                        <span className="material-icons text-xl flex-shrink-0">{cat.icon}</span>
                      ) : (
                        <span className="text-xl flex-shrink-0">📋</span>
                      )}
                      <span className="font-medium text-left flex-1">{cat.libele_categorie}</span>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Colonne Produits - Centre */}

            <div className="flex-1 min-w-0">

              {/* Contenu selon l'onglet actif */}

              <>

              {/* Carrousel Ventes Flash */}
              {(() => {
                const discountedProducts = products.filter(p => p.oldPrice)
                if (discountedProducts.length === 0) return null
                const N = discountedProducts.length
                // Clones uniquement si assez de produits pour défiler (sinon doublons visibles)
                const renderItems = N > 3 ? [...discountedProducts, ...discountedProducts.slice(0, 3)] : discountedProducts
                const activeDot = discountCarouselIndex % N
                const goNext = () => {
                  setDiscountCarouselIndex(i => {
                    const next = i + 1
                    if (next >= N) {
                      setTimeout(() => {
                        setIsFlashResetting(true)
                        setDiscountCarouselIndex(0)
                        setTimeout(() => setIsFlashResetting(false), 50)
                      }, 520)
                    }
                    return next
                  })
                }
                const goPrev = () => setDiscountCarouselIndex(i => (i - 1 + N) % N)
                return (
                  <>
                    <div className="w-full bg-red-50 border border-red-100 rounded-lg px-4 py-3 mb-4 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                        <span className="font-bold text-sm sm:text-base text-[#001F3F]">Deals du moment</span>
                      </div>
                      {N > 3 && (
                        <div className="flex items-center gap-2">
                          <button onClick={goPrev} className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
                          </button>
                          <span className="text-xs text-gray-500">{activeDot + 1}/{N}</span>
                          <button onClick={goNext} className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="overflow-hidden mb-2">
                      <div
                        className="flex"
                        style={{
                          transform: `translateX(-${discountCarouselIndex * (100 / 3)}%)`,
                          transition: isFlashResetting ? 'none' : 'transform 500ms ease-in-out',
                        }}
                      >
                        {renderItems.map((product, renderIdx) => {
                          const priceNum = parseFloat(String(product.price).replace(/[^0-9.]/g, '')) || 0
                          const oldPriceNum = parseFloat(String(product.oldPrice).replace(/[^0-9.]/g, '')) || 0
                          const discount = oldPriceNum > priceNum ? Math.round((1 - priceNum / oldPriceNum) * 100) : 0
                          return (
                            <div key={`${product.id}-${renderIdx}`} className="w-1/3 flex-shrink-0 px-1 sm:px-2">
                              <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-red-100 flex flex-col cursor-pointer h-full" onClick={() => navigate(`/product/${encodeId(product.id)}`)}>
                                <div className="relative h-36 sm:h-44 flex-shrink-0">
                                  <img src={product.image || headerImage} alt={product.name} className="w-full h-full object-cover" />
                                  {discount > 0 && (
                                    <span className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">-{discount}%</span>
                                  )}
                                  {product.etat_appareil_label && (
                                    <span className={`absolute top-2 right-2 px-1.5 py-0.5 rounded text-xs font-bold shadow-md ${
                                      product.etat_appareil === 'neuf' ? 'bg-blue-600 text-white' :
                                      product.etat_appareil === 'scelle' ? 'bg-green-600 text-white' :
                                      product.etat_appareil === 'quasi_neuf' ? 'bg-teal-500 text-white' :
                                      product.etat_appareil === 'excellent' ? 'bg-indigo-500 text-white' :
                                      product.etat_appareil === 'bon' ? 'bg-yellow-500 text-gray-900' :
                                      product.etat_appareil === 'moyen' ? 'bg-orange-500 text-white' :
                                      product.etat_appareil === 'reconditionne' ? 'bg-purple-500 text-white' :
                                      'bg-gray-600 text-white'
                                    }`}>{product.etat_appareil_label}</span>
                                  )}
                                </div>
                                <div className="p-2 flex flex-col flex-1">
                                  <h4 className="font-semibold text-black text-xs sm:text-sm mb-1 line-clamp-1">{product.name}</h4>
                                  <p className="text-gray-400 line-through text-xs">{formatPrice(product.oldPrice)}</p>
                                  <p className="text-red-600 font-bold text-sm sm:text-base">{formatPrice(product.price)}</p>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                    {N > 3 && (
                      <div className="flex justify-center gap-1.5 mb-5">
                        {Array.from({ length: N }).map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setDiscountCarouselIndex(idx)}
                            className={`h-1.5 rounded-full transition-all duration-300 ${idx === activeDot ? 'w-5 bg-red-500' : 'w-1.5 bg-red-200'}`}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )
              })()}

              {/* Bande Meilleures offres */}
              <div className="w-full bg-gray-100 border border-gray-200 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="font-bold text-sm sm:text-base text-[#001F3F]">Meilleures offres</span>
              </div>

              {/* Grille de produits responsive : 2 mobile, 3 desktop */}
              <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-6">{loadingProducts ? (
                  <>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
                        {/* Image skeleton avec shimmer */}
                        <div className="relative w-full h-48 sm:h-52 skeleton-shimmer">
                          <div className="absolute top-2 left-2 w-14 h-6 rounded-full skeleton-shimmer opacity-80"></div>
                          <div className="absolute top-2 right-2 w-20 h-6 rounded-full skeleton-shimmer opacity-80"></div>
                          <div className="absolute bottom-2 left-2 w-8 h-8 rounded-full skeleton-shimmer opacity-80"></div>
                        </div>

                        <div className="p-3 space-y-3">
                          {/* Titre */}
                          <div className="space-y-1.5">
                            <div className="h-4 skeleton-shimmer rounded-full w-3/4"></div>
                            <div className="h-4 skeleton-shimmer rounded-full w-1/2"></div>
                          </div>

                          {/* Prix */}
                          <div className="flex items-center gap-2">
                            <div className="h-6 skeleton-shimmer rounded-full w-24"></div>
                            <div className="h-4 skeleton-shimmer rounded-full w-16 opacity-60"></div>
                          </div>

                          {/* Contact */}
                          <div className="rounded-xl p-2.5 bg-gray-50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full skeleton-shimmer flex-shrink-0"></div>
                              <div className="space-y-1">
                                <div className="h-3 skeleton-shimmer rounded-full w-20"></div>
                                <div className="h-3 skeleton-shimmer rounded-full w-14 opacity-60"></div>
                              </div>
                            </div>
                            <div className="flex gap-1.5">
                              <div className="w-8 h-8 rounded-lg skeleton-shimmer"></div>
                              <div className="w-8 h-8 rounded-lg skeleton-shimmer"></div>
                              <div className="w-8 h-8 rounded-lg skeleton-shimmer"></div>
                            </div>
                          </div>

                          {/* Stats */}
                          <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                            <div className="h-3 skeleton-shimmer rounded-full w-10"></div>
                            <div className="h-3 skeleton-shimmer rounded-full w-8"></div>
                            <div className="h-3 skeleton-shimmer rounded-full w-8"></div>
                            <div className="ml-auto h-3 skeleton-shimmer rounded-full w-16"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                ) : products.length === 0 ? (
                  <div className="col-span-2 text-center py-8 text-gray-400">Aucun produit trouvé.</div>
                ) : (
                  products.map((product) => {
                    const priceNum = typeof product.price === 'string' ? parseFloat(product.price.replace(/[^0-9]/g, '')) : product.price
                    const oldPriceNum = product.oldPrice ? (typeof product.oldPrice === 'string' ? parseFloat(product.oldPrice.replace(/[^0-9]/g, '')) : product.oldPrice) : 0
                    const discount = oldPriceNum > 0 ? Math.round((1 - priceNum / oldPriceNum) * 100) : 0;
                    
                    return (
                    <div
                      key={product.id}
                      className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 text-left w-full border border-gray-100 flex flex-col h-full"
                    >
                      <div 
                        onClick={() => navigate(`/product/${encodeId(product.id)}`)}
                        className="relative h-44 sm:h-52 lg:h-48 xl:h-56 flex-shrink-0 cursor-pointer"
                      >
                        <div className="w-full h-full bg-white flex items-center justify-center overflow-hidden">
                          <img
                            src={product.image || headerImage}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {discount > 0 && (
                          <span className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-red-600 text-white text-xs sm:text-sm md:text-md font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded">
                            -{discount}%
                          </span>
                        )}

                        {/* Badge État dynamique sur l'image */}

                        {(product.etat_appareil_label || product.condition_label) && (
                          <div className={`absolute top-1 right-1 sm:top-2 sm:right-2 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded text-xs sm:text-sm font-bold shadow-md ${
                            product.etat_appareil === 'neuf' ? 'bg-blue-600 text-white' :
                            product.etat_appareil === 'scelle' ? 'bg-green-600 text-white' :
                            product.etat_appareil === 'quasi_neuf' ? 'bg-teal-500 text-white' :
                            product.etat_appareil === 'excellent' ? 'bg-indigo-500 text-white' :
                            product.etat_appareil === 'bon' ? 'bg-yellow-500 text-gray-900' :
                            product.etat_appareil === 'moyen' ? 'bg-orange-500 text-white' :
                            product.etat_appareil === 'usage' ? 'bg-red-500 text-white' :
                            product.etat_appareil === 'reconditionne' ? 'bg-purple-500 text-white' :
                            'bg-gray-600 text-white'
                          }`}>
                            {product.etat_appareil_label || product.condition_label}
                          </div>
                        )}

                        {/* Vues dans l'image */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent pb-2 pt-8 px-2">
                          <div className="flex items-center gap-1 text-white text-xs">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <span className="font-semibold">{product.views || 0}</span>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 flex-1 flex flex-col">
                        <h4 
                          onClick={() => navigate(`/product/${encodeId(product.id)}`)}
                          className="font-semibold text-black text-sm sm:text-base mb-2 line-clamp-2 lg:line-clamp-1 min-h-[2.5rem] lg:min-h-0 cursor-pointer hover:text-[#D4AF37] transition-colors"
                        >
                          {product.name}
                        </h4>
                        
                        {/* Prix */}
                        <div className="mb-2">
                          <p className="text-red-600 font-bold text-sm sm:text-base">{formatPrice(product.price)}</p>
                          {product.oldPrice && (
                            <p className="text-gray-400 line-through text-xs">{formatPrice(product.oldPrice)}</p>
                          )}
                        </div>

                        {/* Section Vendeur avec boutons */}

                        <div className="hidden lg:block mb-3 bg-amber-50 rounded-lg p-2">
                          <div className="flex flex-row items-center justify-between gap-1.5">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-[#001F3F]">
                                {product.client_name || 'Non disponible'}
                              </p>
                              {(product.client_phone) && (
                                <p className="text-xs text-gray-600 mt-0.5">
                                  {product.client_phone}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <button 
                                onClick={(e) => { 
                                  e.stopPropagation();
                                  const phone = product.client_phone;
                                  if (phone) window.location.href = `tel:${phone}`;
                                }}
                                className="bg-[#D4AF37] text-white p-2 rounded hover:bg-[#c09d2a] transition-colors" 
                                title="Appeler"
                              >
                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                </svg>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate('/messages', {
                                    state: {
                                      openConversationWith: {
                                        id:     product.client_id,
                                        name:   product.client_name,
                                        phone:  product.client_phone,
                                        avatar: product.client_photo,
                                        article: {
                                          name:  product.name,
                                          url:   `/product/${encodeId(product.id)}`,
                                          image: product.image,
                                        }
                                      }
                                    }
                                  });
                                }}
                                className="bg-[#D4AF37] text-white p-2 rounded hover:bg-[#c09d2a] transition-colors" 
                                title="Message"
                              >
                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                                </svg>
                              </button>
                              <button 
                                onClick={(e) => { 
                                  e.stopPropagation();
                                  const phone = product.client_phone;
                                  if (phone) {
                                    let digits = phone.replace(/\D/g, '')
                                    if (digits.startsWith('0') && digits.length <= 10) digits = '225' + digits.slice(1)
                                    window.open(`https://wa.me/${digits}`, '_blank')
                                  }
                                }}
                                className="bg-[#D4AF37] text-white p-2 rounded hover:bg-[#c09d2a] transition-colors" 
                                title="WhatsApp"
                              >
                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between border-t pt-2">
                          <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-500">
                            <div className="flex items-center gap-0.5 sm:gap-1">
                              <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                              </svg>
                              <span>{product.likes}</span>
                            </div>
                            <div className="flex items-center gap-0.5 sm:gap-1">
                              <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                              </svg>
                              <span>{product.comments}</span>
                            </div>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-400">{product.date}</p>
                        </div>
                      </div>
                    </div>
                  )})
                )}
              </div>

              {/* Pagination avec numéros de page - Cachée sur mobile */}

              <div className="hidden lg:flex justify-center items-center gap-2 mb-8 mt-10">
                <button 
                  className="w-10 h-10 bg-[#D4AF37] text-white rounded flex items-center justify-center disabled:opacity-50"
                  onClick={() => handlePageChange(Math.max(1, page - 1))}
                  disabled={page === 1}
                >
                  ←
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-10 h-10 rounded flex items-center justify-center font-medium ${
                      page === pageNum
                        ? 'bg-[#D4AF37] text-white'
                        : 'bg-white text-[#001F3F] hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
                
                <button 
                  className="w-10 h-10 bg-[#D4AF37] text-white rounded flex items-center justify-center disabled:opacity-50"
                  onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                >
                  →
                </button>
              </div>
              
              </>

            </div>

            {/* Colonne Publicités - Droite - Sticky - Cachée sur mobile */}

            <div className="hidden lg:block w-full lg:w-80 flex-shrink-0 sticky top-24 self-start">
              <div className="space-y-4">

                {/* Bannières publicitaires du staff */}

                {loadingAdBanners ? (
                  <div className="bg-white rounded-xl overflow-hidden h-48 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
                  </div>
                ) : adBanners.length > 0 ? (
                  <div className="relative bg-white rounded-xl overflow-hidden h-48 shadow-sm group">
                    {/* Image de la bannière */}
                    <a 
                      href={adBanners[adBannerIndex]?.lien_url || '#'} 
                      target={adBanners[adBannerIndex]?.lien_url ? '_blank' : '_self'}
                      rel="noopener noreferrer"
                      className="block h-full"
                    >
                      <img
                        key={adBannerIndex}
                        src={adBanners[adBannerIndex]?.image_url || headerImage}
                        alt={adBanners[adBannerIndex]?.titre || 'Publicité'}
                        className="w-full h-full object-cover transition-all duration-500 ease-in-out animate-fadeIn"
                        onError={(e) => {
                          e.target.src = headerImage
                        }}
                      />
                    </a>
                    
                    {/* Stats en bas */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-6 flex items-center gap-3 text-white text-xs">
                      <span className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                        {adBanners[adBannerIndex]?.views ?? 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>
                        {adBanners[adBannerIndex]?.likes ?? 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                        {adBanners[adBannerIndex]?.comments ?? 0}
                      </span>
                    </div>

                    {/* Bouton Détail au hover */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center z-10">
                      <button
                        onClick={() => navigate(`/staff-banner/${encodeId(adBanners[adBannerIndex]?.id || adBanners[adBannerIndex]?.banniere_id)}`)}
                        className="bg-white text-[#D4AF37] px-6 py-3 rounded-full font-bold shadow-lg transform scale-90 group-hover:scale-100 transition-transform duration-300 flex items-center gap-2 hover:bg-gray-50"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Voir les détails
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl overflow-hidden h-48 flex items-center justify-center border-2 border-dashed border-gray-300">
                    <div className="text-center text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-2 opacity-30" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z"/>
                      </svg>
                      <p className="text-xs font-semibold">Publicité</p>
                      {isAdminOrStaff && <p className="text-xs mt-1 text-[#D4AF37]">Espace réservé - Staff</p>}
                    </div>
                  </div>
                )}
                
                {/* Vidéos publicitaires du staff */}

                {loadingAdVideos ? (
                  <div className="bg-white rounded-xl overflow-hidden h-48 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
                  </div>
                ) : adVideos.length > 0 ? (
                  <div className="relative bg-white rounded-xl overflow-hidden shadow-sm">
                    <div className="relative h-48">
                      {/* Carrousel de vidéos */}
                      <div 
                        className="flex transition-transform duration-500 ease-in-out h-full"
                        style={{ transform: `translateX(-${adVideoIndex * 100}%)` }}
                      >
                        {adVideos.map((video, index) => (
                          <div 
                            key={video.id} 
                            className="w-full flex-shrink-0 h-full cursor-pointer group"
                          >
                            <div className="relative h-full" onClick={() => navigate(`/video/${encodeId(video.id)}`)}>
                              <video
                                src={video.video_url}
                                alt={video.titre}
                                className="w-full h-full object-cover"
                                autoPlay={index === adVideoIndex}
                                muted
                                loop={false}
                                playsInline
                              />
                              {/* Stats en bas */}
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-6 flex items-center gap-3 text-white text-xs">
                                <span className="flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                                  {video.views ?? 0}
                                </span>
                                <span className="flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>
                                  {video.likes ?? 0}
                                </span>
                                <span className="flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                                  {video.comments ?? 0}
                                </span>
                              </div>

                              {/* Overlay play */}
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                                <div className="w-12 h-12 bg-white bg-opacity-90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                  <svg className="w-6 h-6 text-[#D4AF37] ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl overflow-hidden h-48 flex items-center justify-center border-2 border-dashed border-gray-300">
                    <div className="text-center text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-2 opacity-30" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M4 5h16a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1zm11 4.5l-6 4.5V10l6-4.5z"/>
                      </svg>
                      <p className="text-xs font-semibold">Vidéo sponsorisée</p>
                      {isAdminOrStaff && <p className="text-xs mt-1 text-[#D4AF37]">Espace réservé - Staff</p>}
                    </div>
                  </div>
                )}
                
                {/* Publications publicitaires du staff */}

                {loadingAdPosts ? (
                  <div className="bg-white rounded-xl overflow-hidden h-80 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
                  </div>
                ) : adPosts.length > 0 ? (
                  <div 
                    className="relative bg-white rounded-xl overflow-hidden shadow-sm group"
                    onMouseEnter={() => setIsAdPostHovered(true)}
                    onMouseLeave={() => setIsAdPostHovered(false)}
                  >
                    <div className="relative h-80">
                      <img
                        key={adPostIndex}
                        src={adPosts[adPostIndex]?.images?.[0] || headerImage}
                        alt={adPosts[adPostIndex]?.libele || 'Publication'}
                        className="w-full h-full object-cover transition-all duration-500 ease-in-out"
                        onError={(e) => {
                          e.target.src = headerImage
                        }}
                      />
                      
                      {/* Badge de réduction */}
                      {(() => {
                        const currentPost = adPosts[adPostIndex]
                        if (currentPost?.oldPrice && currentPost?.price) {
                          const discount = Math.round((1 - currentPost.price / currentPost.oldPrice) * 100)
                          if (discount > 0) {
                            return (
                              <span className="absolute top-2 left-2 bg-red-600 text-white text-sm font-bold px-2 py-1 rounded z-10">
                                -{discount}%
                              </span>
                            )
                          }
                        }
                        return null
                      })()}
                      
                      {/* Badge état appareil */}
                      {adPosts[adPostIndex]?.etat_label && (
                        <span className={`absolute top-2 right-2 z-10 px-2 py-1 rounded text-xs font-bold shadow-md ${
                          adPosts[adPostIndex].etat_appareil === 'neuf' ? 'bg-blue-600 text-white' :
                          adPosts[adPostIndex].etat_appareil === 'scelle' ? 'bg-green-600 text-white' :
                          adPosts[adPostIndex].etat_appareil === 'quasi_neuf' ? 'bg-teal-500 text-white' :
                          adPosts[adPostIndex].etat_appareil === 'excellent' ? 'bg-indigo-500 text-white' :
                          adPosts[adPostIndex].etat_appareil === 'bon' ? 'bg-yellow-500 text-gray-900' :
                          adPosts[adPostIndex].etat_appareil === 'moyen' ? 'bg-orange-500 text-white' :
                          adPosts[adPostIndex].etat_appareil === 'reconditionne' || adPosts[adPostIndex].etat_appareil === 'reconditionné' ? 'bg-purple-500 text-white' :
                          adPosts[adPostIndex].etat_appareil === 'occasion' ? 'bg-amber-500 text-white' :
                          'bg-gray-600 text-white'
                        }`}>
                          {adPosts[adPostIndex].etat_label}
                        </span>
                      )}

                      {/* Bouton "Voir détails" visible au hover */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center z-20">
                        <button
                          onClick={() => {
                            if (adPosts[adPostIndex]?.id) {
                              navigate(`/product/${encodeId(adPosts[adPostIndex].id)}`)
                            }
                          }}
                          className="px-6 py-3 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-all duration-300 font-semibold text-sm shadow-lg transform hover:scale-105"
                        >
                          Voir détails
                        </button>
                      </div>
                      
                      {/* Overlay avec info */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex flex-col justify-end p-4">
                        <h3 className="text-white font-bold text-sm mb-1 line-clamp-2">
                          {adPosts[adPostIndex]?.libele || adPosts[adPostIndex]?.name}
                        </h3>
                        {adPosts[adPostIndex]?.price > 0 && (
                          <div>
                            {adPosts[adPostIndex]?.oldPrice && (
                              <p className="text-white/60 line-through text-xs">
                                {new Intl.NumberFormat('fr-FR').format(adPosts[adPostIndex].oldPrice)} FCFA
                              </p>
                            )}
                            <p className="text-[#FFD700] font-bold text-lg">
                              {formatPrice(adPosts[adPostIndex].price)}
                            </p>
                          </div>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-white/80 text-xs">
                          <span className="flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                            {adPosts[adPostIndex]?.vues || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>
                            {adPosts[adPostIndex]?.likes_count || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                            {adPosts[adPostIndex]?.comments_count || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl overflow-hidden h-80 flex items-center justify-center border-2 border-dashed border-gray-300">
                    <div className="text-center text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-2 opacity-30" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M4 5h16a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1zm11 4.5l-6 4.5V10l6-4.5z"/>
                      </svg>
                      <p className="text-xs font-semibold">Publication sponsorisée</p>
                      {isAdminOrStaff && <p className="text-xs mt-1 text-[#D4AF37]">Espace réservé - Staff</p>}
                    </div>
                  </div>
                )}
                
                {/* Mes contacts récents */}

                <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                  <div className="p-3 border-b border-gray-100">
                    <h3 className="font-semibold text-[#001F3F] text-sm">Vos contacts récents</h3>
                  </div>
                  
                  <div className="p-3">
                    {loadingContacts ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#D4AF37]"></div>
                      </div>
                    ) : recentContacts.length > 0 ? (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {recentContacts.map((contact) => {
                          // Créer les initiales à partir du prénom et nom
                          const getInitials = () => {
                            const firstName = contact.first_name || ''
                            const lastName = contact.name || ''
                            const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase()
                            return initials || '?'
                          }
                          
                          return (
                          <button
                            key={contact.id}
                            onClick={() => navigate('/messages', { 
                              state: { 
                                openConversationWith: {
                                  id: contact.id,
                                  name: contact.full_name || contact.name,
                                  phone: contact.phone,
                                  avatar: contact.avatar
                                }
                              }
                            })}
                            className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <div className="relative flex-shrink-0">
                              {contact.avatar ? (
                                <img 
                                  src={contact.avatar.startsWith('http') ? contact.avatar : mediaUrl(contact.avatar)}
                                  alt={contact.full_name || contact.name}
                                  className="w-10 h-10 rounded-full object-cover"
                                  onError={(e) => { 
                                    e.target.style.display = 'none'
                                    e.target.nextSibling.style.display = 'flex'
                                  }}
                                />
                              ) : null}
                              <div 
                                className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] flex items-center justify-center text-white font-semibold text-sm"
                                style={{ display: contact.avatar ? 'none' : 'flex' }}
                              >
                                {getInitials()}
                              </div>
                              {contact.is_mutual && (
                                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[#D4AF37] rounded-full flex items-center justify-center">
                                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <p className="font-medium text-sm text-[#001F3F] truncate">
                                {contact.full_name || contact.name || 'Utilisateur'}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {contact.phone || 'Contact'}
                              </p>
                            </div>
                            <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        )})}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <svg className="w-12 h-12 mx-auto mb-2 opacity-30" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                        </svg>
                        <p className="text-xs">Aucun contact récent</p>
                        <p className="text-xs mt-1">Abonnez-vous à d'autres utilisateurs</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Nouveau Poste */}

      {showNewPostModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl">

            {/* Header */}

            <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200">
              <h2 className="text-lg md:text-2xl font-bold text-[#001F3F] flex-1 text-center">Publier une annonce</h2>
              <button 
                onClick={() => setShowNewPostModal(false)}
                className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-full transition-colors flex-shrink-0"
              >
                <svg className="w-5 h-5 md:w-6 md:h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Content */}

            <div className="p-4 md:p-8 lg:p-12">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-6">

                {/* Images */}

                <button 
                  onClick={() => {
                    setShowNewPostModal(false)
                    navigate('/create-post')
                  }}
                  className="group flex flex-col items-center gap-2 md:gap-4 p-4 md:p-8 bg-gradient-to-br from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 rounded-xl md:rounded-2xl transition-all duration-300 border-2 border-transparent hover:border-blue-300 hover:shadow-lg transform hover:-translate-y-1"
                >
                  <div className="w-14 h-14 md:w-20 md:h-20 bg-[#D4AF37] rounded-xl md:rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-8 h-8 md:w-12 md:h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                    </svg>
                  </div>
                  <span className="text-sm md:text-lg font-semibold text-[#001F3F] group-hover:text-[#D4AF37] transition-colors">Images</span>
                </button>

                {/* Vidéos */}

                <button 
                  onClick={() => {
                    setShowNewPostModal(false)
                    navigate('/create-video')
                  }}
                  className="group flex flex-col items-center gap-2 md:gap-4 p-4 md:p-8 bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 rounded-xl md:rounded-2xl transition-all duration-300 border-2 border-transparent hover:border-purple-300 hover:shadow-lg transform hover:-translate-y-1"
                >
                  <div className="w-14 h-14 md:w-20 md:h-20 bg-[#D4AF37] rounded-xl md:rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-8 h-8 md:w-12 md:h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4zM14 13h-3v3H9v-3H6v-2h3V8h2v3h3v2z"/>
                    </svg>
                  </div>
                  <span className="text-sm md:text-lg font-semibold text-[#001F3F] group-hover:text-[#D4AF37] transition-colors">Vidéos</span>
                </button>

                {/* Bannières */}

                <button 
                  onClick={() => {
                    setShowNewPostModal(false)
                    navigate('/create-banner')
                  }}
                  className="group flex flex-col items-center gap-2 md:gap-4 p-4 md:p-8 bg-gradient-to-br from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 rounded-xl md:rounded-2xl transition-all duration-300 border-2 border-transparent hover:border-amber-300 hover:shadow-lg transform hover:-translate-y-1"
                >
                  <div className="w-14 h-14 md:w-20 md:h-20 bg-[#D4AF37] rounded-xl md:rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-8 h-8 md:w-12 md:h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/>
                    </svg>
                  </div>
                  <span className="text-sm md:text-lg font-semibold text-[#001F3F] group-hover:text-[#D4AF37] transition-colors">Bannières</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Message */}

      {showMessageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-lg w-full mx-4 h-[600px] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-3">
                <img src={avatarPlaceholder} alt="LIGDI Store" className="w-10 h-10 rounded-full" />
                <span className="font-semibold">LIGDI Store</span>
              </div>
              <button onClick={() => setShowMessageModal(false)}>×</button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="text-center text-sm text-gray-500">01/03/2025 à 08H05</div>
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg px-4 py-2 max-w-xs">
                  <p className="text-sm">Bonjour Monsieur</p>
                </div>
              </div>
              <div className="flex justify-end">
                <div className="bg-[#D4AF37] text-white rounded-lg px-4 py-2 max-w-xs">
                  <p className="text-sm">Bonjour Monsieur, comment puis-je vous aider ?</p>
                </div>
              </div>
            </div>
            <div className="border-t p-4 flex gap-2">
              <input
                type="text"
                placeholder="Votre message..."
                className="flex-1 px-4 py-2 border rounded-lg"
              />
              <button className="w-12 h-12 bg-[#D4AF37] rounded-lg">→</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DashboardPage
